import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Modal, Button, Table } from "reactstrap";
import ClipLoader from "react-spinners/ClipLoader";

import api from "../../../Api.js";
import UnitSkillsReport from "../modal/unit/UnitSkillsReport.jsx";
import ModalEvaluation from "../modal/ModalEvaluation.jsx";

export default function SettingsModal({
  isOpen,
  classroom,
  translate = {},
  onClose,
  notify,
  onOpenUnitSkills,
  onOpenEvaluation,
}) {
  const [options, setOptions] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [unitsError, setUnitsError] = useState("");
  const [openUnitSkillsModalLocal, setOpenUnitSkillsModalLocal] = useState(false);
  const [unitSkillsTarget, setUnitSkillsTarget] = useState(null);
  const [openEvaluationModalLocal, setOpenEvaluationModalLocal] = useState(false);
  const [evaluationTarget, setEvaluationTarget] = useState(null);

  const classroomId = classroom?.id;
  const classroomCourseId = classroom?.course;

  const sendNotification = useCallback(
    (payload) => {
      if (typeof notify === "function" && payload) {
        notify(payload);
      }
    },
    [notify]
  );

  const loadData = useCallback(
    async ({ showLoading = false } = {}) => {
      if (!classroomId) {
        return;
      }

      if (showLoading) {
        setLoading(true);
      }
      setMutating(true);
      setUnitsError("");

      try {
        const [optionsRes, unitsRes] = await Promise.all([
          api.getClassRoomOptions(classroomId),
          classroomCourseId
            ? api.getUnitsCourse(classroomCourseId)
            : Promise.resolve({ data: [] }),
        ]);

        setOptions(Array.isArray(optionsRes?.data) ? optionsRes.data : []);
        setUnits(Array.isArray(unitsRes?.data) ? unitsRes.data : []);
      } catch (error) {
        console.error("Erro ao carregar configurações da turma", error);
        const message =
          error?.response?.data?.msg ||
          translate["erro_ao_carregar_units"] ||
          "Erro ao carregar as units.";
        setUnits([]);
        setUnitsError(message);
      } finally {
        setMutating(false);
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [classroomId, classroomCourseId, translate]
  );

  useEffect(() => {
    if (!isOpen || !classroomId) {
      if (!isOpen) {
        setOptions([]);
        setUnits([]);
        setUnitsError("");
      }
      return;
    }

    let cancelled = false;
    (async () => {
      await loadData({ showLoading: true });
      if (cancelled) {
        return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, classroomId, loadData]);

  const isOptionActive = useCallback(
    (name, value = "1") =>
      options.some(
        (opt) =>
          opt?.name === name && String(opt?.value) === String(value)
      ),
    [options]
  );

  const handleToggleOption = useCallback(
    async (name, value = "1") => {
      if (!classroomId) {
        return;
      }

      setMutating(true);
      try {
        if (isOptionActive(name, value)) {
          await api.deleteClassRoomOption(classroomId, { name, value });
        } else {
          await api.insertClassRoomOption(classroomId, { name, value });
        }
        await loadData();
      } catch (error) {
        console.error("Erro ao alternar opção", name, value, error);
        const message =
          error?.response?.data?.msg ||
          translate["erro_ao_alterar_configuracao"] ||
          "Não foi possível alterar a configuração.";
        sendNotification({
          place: "tr",
          message: (
            <div className="alert-text">
              <span className="alert-title" data-notify="title">
                {(translate["erro"] || "Erro") + "!"}
              </span>
              <span data-notify="message">{message}</span>
            </div>
          ),
          type: "danger",
          icon: "fas fa-exclamation",
          autoDismiss: 7,
        });
      } finally {
        setMutating(false);
      }
    },
    [classroomId, isOptionActive, loadData, sendNotification, translate]
  );

  const handleToggleUnitOption = useCallback(
    async (name, value) => {
      await handleToggleOption(name, String(value));
    },
    [handleToggleOption]
  );

  const handleLockAllUnits = useCallback(async () => {
    if (!classroomId || units.length === 0) {
      return;
    }

    setMutating(true);
    try {
      for (const unit of units) {
        const key = String(unit.id);
        if (!isOptionActive("lock_unit", key)) {
          await api.insertClassRoomOption(classroomId, {
            name: "lock_unit",
            value: key,
          });
        }
      }
      await loadData();
    } catch (error) {
      console.error("Erro ao bloquear todas as units", error);
      const message =
        error?.response?.data?.msg ||
        translate["erro_ao_bloquear_units"] ||
        "Não foi possível bloquear todas as units.";
      sendNotification({
        place: "tr",
        message: (
          <div className="alert-text">
            <span className="alert-title" data-notify="title">
              {(translate["erro"] || "Erro") + "!"}
            </span>
            <span data-notify="message">{message}</span>
          </div>
        ),
        type: "danger",
        icon: "fas fa-exclamation",
        autoDismiss: 7,
      });
    } finally {
      setMutating(false);
    }
  }, [classroomId, isOptionActive, loadData, sendNotification, translate, units]);

  const handleUnlockAllUnits = useCallback(async () => {
    if (!classroomId || units.length === 0) {
      return;
    }

    setMutating(true);
    try {
      for (const unit of units) {
        const key = String(unit.id);
        if (isOptionActive("lock_unit", key)) {
          await api.deleteClassRoomOption(classroomId, {
            name: "lock_unit",
            value: key,
          });
        }
      }
      await loadData();
    } catch (error) {
      console.error("Erro ao desbloquear todas as units", error);
      const message =
        error?.response?.data?.msg ||
        translate["erro_ao_desbloquear_units"] ||
        "Não foi possível desbloquear todas as units.";
      sendNotification({
        place: "tr",
        message: (
          <div className="alert-text">
            <span className="alert-title" data-notify="title">
              {(translate["erro"] || "Erro") + "!"}
            </span>
            <span data-notify="message">{message}</span>
          </div>
        ),
        type: "danger",
        icon: "fas fa-exclamation",
        autoDismiss: 7,
      });
    } finally {
      setMutating(false);
    }
  }, [classroomId, isOptionActive, loadData, sendNotification, translate, units]);

  const closeModal = useCallback(() => {
    if (typeof onClose === "function") {
      onClose();
    }
  }, [onClose]);

  const unitsStatus = useMemo(() => {
    return units.map((unit) => {
      const id = String(unit.id);
      return {
        data: unit,
        locked: isOptionActive("lock_unit", id),
        hidden: isOptionActive("hidden_unit", id),
        superunlock: isOptionActive("superunlock_unit", id),
      };
    });
  }, [isOptionActive, units]);

  return (
    <>
      <Modal
      className="modal-dialog-centered modal-lg classroom-settings-modal"
      isOpen={Boolean(isOpen)}
      toggle={closeModal}
    >
      <div className="modal-header pb-2">
        <div>
          <h5 className="modal-title mb-1">
            {(translate["configuracoes_da_turma"] || "Configurações da Turma") +
              (classroomId ? ` #${classroomId}` : "")}
          </h5>
          {classroom?.name && (
            <small className="text-muted">{classroom.name}</small>
          )}
        </div>
        <button className="close" onClick={closeModal}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div className="modal-body pt-0">
        {loading ? (
          <div className="py-5 text-center">
            <ClipLoader size={40} color="#5e72e4" />
            <p className="mt-3 mb-0">
              {translate["carregando"] || "Carregando..."}
            </p>
          </div>
        ) : (
          <>
            <div className="d-flex flex-column flex-md-row mb-4 gap-24">
              <div className="flex-fill settings-box">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="settings-section-title mb-0">
                    {translate["opcoes_gerais"] || "Opções Gerais"}
                  </h6>
                </div>
                <div className="d-flex flex-wrap gap-8">
                  <Button
                    size="sm"
                    className="shadow-none"
                    color={isOptionActive("lock_comments") ? "danger" : "secondary"}
                    onClick={() => handleToggleOption("lock_comments")}
                    disabled={mutating}
                  >
                    <i className="fas fa-comment-slash mr-1"></i>
                    {isOptionActive("lock_comments")
                      ? translate["desbloquear_comentarios"] ||
                        "Desbloquear Comentários"
                      : translate["bloquear_comentarios"] ||
                        "Bloquear Comentários"}
                  </Button>
                  <Button
                    size="sm"
                    className="shadow-none"
                    color={isOptionActive("hidden_chat") ? "danger" : "secondary"}
                    onClick={() => handleToggleOption("hidden_chat")}
                    disabled={mutating}
                  >
                    <i className="fas fa-comments mr-1"></i>
                    {isOptionActive("hidden_chat")
                      ? translate["desbloquear_chat"] || "Desbloquear Chat"
                      : translate["bloquear_chat"] || "Bloquear Chat"}
                  </Button>
                </div>
              </div>

              <div className="flex-fill settings-box">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="settings-section-title mb-0">
                    {translate["acoes_rapidas_units"] || "Ações Rápidas (Units)"}
                  </h6>
                </div>
                <div className="d-flex flex-wrap gap-8">
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={handleLockAllUnits}
                    disabled={mutating}
                  >
                    <i className="fas fa-lock mr-1" />
                    {translate["bloquear_todas"] || "Bloquear todas"}
                  </Button>
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={handleUnlockAllUnits}
                    disabled={mutating}
                  >
                    <i className="fas fa-lock-open mr-1" />
                    {translate["desbloquear_todas"] || "Desbloquear todas"}
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    onClick={() => loadData()}
                    disabled={mutating}
                  >
                    <i className="fas fa-sync-alt mr-1" />
                    {translate["recarregar"] || "Recarregar"}
                  </Button>
                </div>
                <div className="legend mt-3">
                  <span className="legend-label mr-2">
                    {translate["legenda_label"] || "Legenda:"}
                  </span>
                  <span className="badge badge-success mr-1 mb-1">
                    {translate["liberada"] || "Liberada"}
                  </span>
                  <span className="badge badge-danger mr-1 mb-1">
                    {translate["bloqueada"] || "Bloqueada"}
                  </span>
                  <span className="badge badge-warning mr-1 mb-1">
                    {translate["oculta"] || "Oculta"}
                  </span>
                  <span className="badge badge-info">SuperUnlock</span>
                </div>
              </div>
            </div>

            <div className="settings-units-wrapper">
              <h6 className="settings-section-title mb-2">
                {translate["units"] || "Units"}
              </h6>
              <div className="settings-units-table">
                {mutating ? (
                  <div className="py-5 text-center">
                    <ClipLoader size={35} color="#5e72e4" />
                    <p className="mt-3 mb-0">
                      {translate["carregando_units"] || "Carregando units..."}
                    </p>
                  </div>
                ) : unitsError ? (
                  <div className="py-4 text-center">
                    <i className="fas fa-exclamation-triangle fa-2x text-warning mb-2"></i>
                    <p className="mb-2 text-danger" style={{ whiteSpace: "pre-line" }}>
                      {unitsError}
                    </p>
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => loadData()}
                      disabled={mutating}
                    >
                      <i className="fas fa-sync-alt mr-1" />
                      {translate["tentar_novamente"] || "Tentar novamente"}
                    </Button>
                  </div>
                ) : (
                  <Table
                    size="sm"
                    responsive
                    className="table-flush mb-0 units-table"
                  >
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: "38%" }}>
                          {translate["unit"] || "Unit"}
                        </th>
                        <th style={{ width: "20%" }}>
                          {translate["status"] || "Status"}
                        </th>
                        <th style={{ width: "42%" }} className="text-center">
                          {translate["acoes"] || "Ações"}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {unitsStatus.map(({ data: unit, locked, hidden, superunlock }) => (
                        <tr key={unit.id} className="unit-row">
                          <td className="align-middle">
                            <div className="d-flex align-items-center">
                              <span className="unit-name text-truncate" title={unit.name}>
                                {unit.name || `Unit #${unit.id}`}
                              </span>
                            </div>
                          </td>
                          <td className="align-middle">
                            <div className="d-flex flex-wrap status-badges">
                              {hidden && (
                                <span className="badge badge-warning mr-1 mb-1">
                                  {translate["oculta"] || "Oculta"}
                                </span>
                              )}
                              {locked && (
                                <span className="badge badge-danger mr-1 mb-1">
                                  {translate["bloqueada"] || "Bloqueada"}
                                </span>
                              )}
                              {superunlock && (
                                <span className="badge badge-info mr-1 mb-1">
                                  SuperUnlock
                                </span>
                              )}
                              {!locked && !hidden && (
                                <span className="badge badge-success mb-1">
                                  {translate["liberada"] || "Liberada"}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="align-middle text-center">
                            <div className="btn-group btn-group-sm unit-actions">
                              <Button
                                color={locked ? "danger" : "secondary"}
                                onClick={() => handleToggleUnitOption("lock_unit", unit.id)}
                                disabled={mutating}
                                title={
                                  locked
                                    ? translate["desbloquear_unit"] ||
                                      "Desbloquear unit"
                                    : translate["bloquear_unit"] ||
                                      "Bloquear unit"
                                }
                                className="action-btn"
                              >
                                <i className={`fas fa-${locked ? "lock" : "lock-open"}`} />
                              </Button>
                              <Button
                                color={superunlock ? "info" : "secondary"}
                                onClick={() => handleToggleUnitOption("superunlock_unit", unit.id)}
                                disabled={mutating}
                                title="SuperUnlock"
                                className="action-btn"
                              >
                                <i className="fas fa-bolt" />
                              </Button>
                              <Button
                                color={hidden ? "warning" : "secondary"}
                                onClick={() => handleToggleUnitOption("hidden_unit", unit.id)}
                                disabled={mutating}
                                title={
                                  hidden
                                    ? translate["desocultar_unit"] ||
                                      "Desocultar unit"
                                    : translate["ocultar_unit"] ||
                                      "Ocultar unit"
                                }
                                className="action-btn"
                              >
                                <i className={`fas fa-eye${hidden ? "-slash" : ""}`} />
                              </Button>
                              <Button
                                color="secondary"
                                onClick={() => {
                                  if (typeof onOpenUnitSkills === "function")
                                    onOpenUnitSkills(unit);
                                  setUnitSkillsTarget(unit);
                                  setOpenUnitSkillsModalLocal(true);
                                }}
                                disabled={mutating}
                                title={
                                  translate["relatorio_de_habilidades"] ||
                                  "Relatório de Habilidades"
                                }
                                className="action-btn"
                              >
                                <i className="fas fa-stream" />
                              </Button>
                              <Button
                                color="secondary"
                                onClick={() => {
                                  if (typeof onOpenEvaluation === "function")
                                    onOpenEvaluation(unit);
                                  setEvaluationTarget(unit);
                                  setOpenEvaluationModalLocal(true);
                                }}
                                disabled={mutating}
                                title={translate["avaliacao"] || "Avaliação"}
                                className="action-btn"
                              >
                                <i className="fas fa-star" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {unitsStatus.length === 0 && (
                        <tr>
                          <td colSpan="3" className="text-center text-muted py-4">
                            {translate["nenhuma_unit_encontrada"] ||
                              "Nenhuma unit encontrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <div className="modal-footer py-2">
        <Button color="default" size="sm" onClick={closeModal}>
          {translate["fechar"] || "Fechar"}
        </Button>
      </div>
      </Modal>
      {openUnitSkillsModalLocal && (
        <UnitSkillsReport
          isOpen={openUnitSkillsModalLocal}
          onClose={() => {
            setOpenUnitSkillsModalLocal(false);
            setUnitSkillsTarget(null);
          }}
          unitId={unitSkillsTarget?.id}
        />
      )}
      {openEvaluationModalLocal && (
        <ModalEvaluation
          data={{ open: true, unit: evaluationTarget?.id, unitname: evaluationTarget?.name || "" }}
          classroom={classroomId}
          close={() => {
            setOpenEvaluationModalLocal(false);
            setEvaluationTarget(null);
          }}
        />
      )}
    </>
  );
}
